import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Calendar, Users, FileText, TrendingUp } from 'lucide-react';
import { appointmentsAPI, patientsAPI, invoicesAPI } from '../services/api';
import { format } from 'date-fns';

function Dashboard() {
  const { data: todayAppointments, isLoading: loadingAppointments } = useQuery({
    queryKey: ['appointments', 'today'],
    queryFn: appointmentsAPI.getToday
  });

  const { data: patientsData } = useQuery({
    queryKey: ['patients'],
    queryFn: () => patientsAPI.getAll()
  });

  const { data: revenueStats } = useQuery({
    queryKey: ['invoices', 'stats'],
    queryFn: invoicesAPI.getStats
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const stats = [
    {
      title: 'Today\'s Appointments',
      value: todayAppointments?.appointments?.length || 0,
      icon: Calendar,
      color: 'var(--primary)',
      bg: 'rgba(0, 102, 255, 0.1)'
    },
    {
      title: 'Total Patients',
      value: patientsData?.patients?.length || 0,
      icon: Users,
      color: 'var(--accent)',
      bg: 'rgba(0, 229, 184, 0.1)'
    },
    {
      title: 'This Week',
      value: revenueStats?.weeklyAppointments || 0,
      subtitle: 'appointments',
      icon: FileText,
      color: 'var(--success)',
      bg: 'rgba(16, 185, 129, 0.1)'
    },
    {
      title: 'Revenue This Week',
      value: formatCurrency(revenueStats?.weeklyRevenue),
      icon: TrendingUp,
      color: 'var(--warning)',
      bg: 'rgba(245, 158, 11, 0.1)'
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h1>Dashboard</h1>
        <p className="text-muted">Welcome back! Here's what's happening today.</p>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: stat.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon size={24} color={stat.color} />
                </div>
                <div>
                  <p className="text-muted text-small">{stat.title}</p>
                  <h2 style={{ marginBottom: 0 }}>{stat.value}</h2>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Today's Schedule */}
      <div className="card">
        <div className="card-header">
          <h3>Today's Schedule</h3>
          <Link to="/calendar" className="btn btn-secondary btn-sm">
            View Calendar
          </Link>
        </div>

        {loadingAppointments ? (
          <div className="spinner"></div>
        ) : todayAppointments?.appointments?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {todayAppointments.appointments.map((apt) => (
              <div
                key={apt.id}
                style={{
                  padding: '1rem',
                  background: 'var(--gray-50)',
                  borderRadius: 'var(--radius)',
                  borderLeft: '4px solid var(--primary)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--primary)', marginBottom: '0.25rem' }}>
                    {apt.startTime} - {apt.endTime}
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {apt.patient.firstName} {apt.patient.lastName}
                  </div>
                  <div className="text-muted text-small">
                    {apt.type.replace('_', ' ')} • {apt.treatmentArea || 'General'}
                  </div>
                </div>
                <span className={`badge badge-${
                  apt.status === 'completed' ? 'success' :
                  apt.status === 'confirmed' ? 'primary' :
                  apt.status === 'in_progress' ? 'warning' : 'gray'
                }`}>
                  {apt.status}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-light)' }}>
            <Calendar size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
            <p>No appointments scheduled for today</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
